import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtGNF } from "@/lib/format";
import { ABtn, AInput, ALabel, ASelect, Panel } from "./AdminLayout";

const EMPTY = {
  name: "",
  sku: "",
  barcode: "",
  description: "",
  category_id: "",
  images: [],
  cost_price: 0,
  price: 0,
  wholesale_price: "",
  wholesale_min_qty: "",
  compare_price: "",
  stock: 0,
  low_stock_threshold: 5,
  location: "",
  is_active: true,
  is_featured: false,
  is_adult: false,
  deal_ends_at: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/admin/products", { params: { search: search || undefined } }),
        api.get("/categories", { params: { all: true } }),
      ]);
      setProducts(p.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openNew = () => {
    setForm({ ...EMPTY, category_id: categories[0]?.id || "" });
    setImageUrl("");
  };

  useEffect(() => {
    if (searchParams.get("nouveau") && categories.length > 0 && !form) {
      openNew();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, searchParams]);

  const openEdit = (p) => {
    setForm({
      ...EMPTY,
      ...p,
      wholesale_price: p.wholesale_price ?? "",
      wholesale_min_qty: p.wholesale_min_qty ?? "",
      compare_price: p.compare_price ?? "",
      location: p.location ?? "",
      deal_ends_at: p.deal_ends_at ? p.deal_ends_at.slice(0, 16) : "",
    });
    setImageUrl("");
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      ...form,
      cost_price: Number(form.cost_price) || 0,
      price: Number(form.price) || 0,
      stock: Number(form.stock) || 0,
      low_stock_threshold: Number(form.low_stock_threshold) || 0,
      wholesale_price: form.wholesale_price === "" ? null : Number(form.wholesale_price),
      wholesale_min_qty: form.wholesale_min_qty === "" ? null : Number(form.wholesale_min_qty),
      compare_price: form.compare_price === "" ? null : Number(form.compare_price),
      deal_ends_at: form.deal_ends_at || null,
    };
    delete payload.id;
    delete payload.created_at;
    delete payload.category_name;
    try {
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(form.id ? "Produit modifié" : "Produit créé");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.name} » ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produit supprimé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/products/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modele-import-articles-empire-shop.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const importCatalog = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.created} créé(s), ${data.updated} mis à jour`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setImporting(false);
    }
  };

  const importDemo = async () => {
    try {
      const { data } = await api.post("/admin/seed-demo-products");
      toast.success(`${data.created} article(s) importé(s)`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadFile = async (file) => {    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image téléversée");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-products-page">
      <Panel
        title="Produits EMPIRE-SHOP"
        subtitle={`${products.length} produit(s)`}
        actions={
          <>
            <AInput
              placeholder="Rechercher nom ou SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56"
              data-testid="admin-product-search"
            />
            <ABtn onClick={openNew} data-testid="admin-new-product-button">
              <Plus className="h-4 w-4" /> Nouveau produit
            </ABtn>
            {products.length === 0 && (
              <ABtn variant="gold" onClick={importDemo} data-testid="admin-import-demo-button">
                Importer des articles de démonstration
              </ABtn>
            )}
          </>
        }
        testid="admin-products-panel"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400">
              <tr className="border-b border-slate-800">
                <th className="py-2">Produit</th>
                <th className="py-2">SKU</th>
                <th className="py-2">Catégorie</th>
                <th className="py-2 text-right">Achat</th>
                <th className="py-2 text-right">Vente</th>
                <th className="py-2 text-right">Gros</th>
                <th className="py-2 text-right">Stock</th>
                <th className="py-2">Emplacement</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const low = p.stock <= p.low_stock_threshold;
                return (
                  <tr key={p.id} className="border-b border-slate-800/60 hover:bg-slate-800/40" data-testid={`admin-product-row-${p.sku}`}>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-slate-800">
                          {(p.images || [])[0] && <img src={mediaUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}
                        </div>
                        <div>
                          <p className="line-clamp-1 font-medium text-white">{p.name}</p>
                          {!p.is_active && <span className="text-[10px] text-rose-400">inactif</span>}
                        </div>
                      </div>
                    </td>
                    <td className="font-num py-2 text-slate-300">{p.sku}</td>
                    <td className="py-2 text-slate-300">{p.category_name}</td>
                    <td className="font-num py-2 text-right text-slate-400">{fmtGNF(p.cost_price || 0)}</td>
                    <td className="font-num py-2 text-right font-semibold text-emerald-400">{fmtGNF(p.price)}</td>
                    <td className="font-num py-2 text-right text-gold">
                      {p.wholesale_price ? `${fmtGNF(p.wholesale_price)} (≥${p.wholesale_min_qty})` : "—"}
                    </td>
                    <td className="font-num py-2 text-right">
                      <span className={low ? "text-rose-400" : "text-slate-200"}>
                        {low && <AlertTriangle className="mr-1 inline h-3 w-3" />}
                        {p.stock}
                      </span>
                    </td>
                    <td className="font-num py-2 text-slate-400">{p.location || "—"}</td>
                    <td className="py-2">
                      <div className="flex justify-end gap-1">
                        <ABtn variant="ghost" onClick={() => openEdit(p)} data-testid={`admin-edit-product-${p.sku}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </ABtn>
                        <ABtn variant="danger" onClick={() => remove(p)} data-testid={`admin-delete-product-${p.sku}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </ABtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        title="Importer mon stock (Excel / CSV)"
        subtitle="Téléchargez le modèle, remplissez vos articles puis envoyez le fichier. Un SKU déjà présent est mis à jour."
        testid="admin-import-panel"
      >
        <div className="flex flex-wrap items-center gap-2">
          <ABtn variant="ghost" onClick={downloadTemplate} data-testid="admin-download-template-button">
            <Upload className="h-4 w-4 rotate-180" /> Télécharger le modèle Excel
          </ABtn>
          <label className="ds-btn-ghost cursor-pointer border-gold/60 text-gold" data-testid="admin-import-file-label">
            <Upload className="h-4 w-4" /> {importing ? "Import en cours…" : "Envoyer mon fichier"}
            <input
              type="file"
              accept=".xlsx,.xlsm,.csv"
              className="hidden"
              onChange={(e) => importCatalog(e.target.files?.[0])}
              data-testid="admin-import-file-input"
            />
          </label>
        </div>
        {importResult && (
          <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950 p-3 text-sm" data-testid="admin-import-result">
            <p className="text-emerald-400">
              {importResult.created} article(s) créé(s) · {importResult.updated} mis à jour
              {importResult.error_count ? ` · ${importResult.error_count} ligne(s) en erreur` : ""}
            </p>
            {(importResult.errors || []).length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-rose-300">
                {importResult.errors.map((err, i) => (
                  <li key={i}>Ligne {err.ligne} {err.sku ? `(${err.sku})` : ""} : {err.erreur}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Panel>

      {form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" data-testid="admin-product-modal">
          <div className="w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">
                {form.id ? "Modifier le produit" : "Nouveau produit"}
              </h3>
              <ABtn variant="ghost" onClick={() => setForm(null)} data-testid="admin-product-modal-close">
                <X className="h-4 w-4" />
              </ABtn>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <ALabel>Nom du produit *</ALabel>
                <AInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name" />
              </div>
              <div>
                <ALabel>SKU / Référence *</ALabel>
                <AInput value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-form-sku" />
              </div>
              <div>
                <ALabel>Catégorie *</ALabel>
                <ASelect value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="product-form-category">
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </ASelect>
              </div>
              <div className="sm:col-span-2">
                <ALabel>Description</ALabel>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-brand"
                  data-testid="product-form-description"
                />
              </div>
              <NumField label="Prix d'achat (GNF)" value={form.cost_price} onChange={(v) => setForm({ ...form, cost_price: v })} testid="product-form-cost" />
              <NumField label="Prix de vente (GNF) *" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="product-form-price" />
              <NumField label="Prix de gros (GNF)" value={form.wholesale_price} onChange={(v) => setForm({ ...form, wholesale_price: v })} testid="product-form-wholesale" />
              <NumField label="Quantité min. de gros" value={form.wholesale_min_qty} onChange={(v) => setForm({ ...form, wholesale_min_qty: v })} testid="product-form-wholesale-qty" />
              <NumField label="Ancien prix (promo)" value={form.compare_price} onChange={(v) => setForm({ ...form, compare_price: v })} testid="product-form-compare" />
              <div className="sm:col-span-2 rounded-xl border border-rose-900/40 bg-rose-950/20 p-3">
                <ALabel>Offre du jour — fin du compte à rebours</ALabel>
                <AInput
                  type="datetime-local"
                  value={form.deal_ends_at || ""}
                  onChange={(e) => setForm({ ...form, deal_ends_at: e.target.value })}
                  data-testid="product-form-deal-ends"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Renseignez une date de fin + un « Ancien prix » pour afficher ce produit sur la page <strong className="text-rose-300">Offres du jour</strong> avec un compte à rebours. Laissez vide pour retirer l'offre.
                </p>
              </div>
              <NumField label="Quantité en stock" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="product-form-stock" />
              <NumField label="Seuil d'alerte" value={form.low_stock_threshold} onChange={(v) => setForm({ ...form, low_stock_threshold: v })} testid="product-form-threshold" />
              <div>
                <ALabel>Code-barres</ALabel>
                <AInput value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="6941487…" data-testid="product-form-barcode" />
              </div>
              <div>
                <ALabel>Emplacement entrepôt</ALabel>
                <AInput value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="A1-R2-E03" data-testid="product-form-location" />
              </div>

              <div className="sm:col-span-2">
                <ALabel>Photos</ALabel>
                <div className="mt-1 flex flex-wrap gap-2">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-slate-700">
                      <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })}
                        className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-rose-600 text-white"
                        data-testid={`product-form-remove-image-${i}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-xl border border-dashed border-slate-600 text-slate-400 hover:bg-slate-800">
                    <Upload className="h-5 w-5" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                      data-testid="product-form-upload-input"
                    />
                  </label>
                </div>
                <div className="mt-2 flex gap-2">
                  <AInput
                    placeholder="…ou coller une URL d'image"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    data-testid="product-form-image-url"
                  />
                  <ABtn
                    variant="ghost"
                    onClick={() => {
                      if (!imageUrl.trim()) return;
                      setForm({ ...form, images: [...form.images, imageUrl.trim()] });
                      setImageUrl("");
                    }}
                    data-testid="product-form-add-image-url"
                  >
                    Ajouter
                  </ABtn>
                </div>
              </div>

              <div className="flex gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="product-form-active" />
                  Produit actif (visible en boutique)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="product-form-featured" />
                  Mis en avant
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={form.is_adult} onChange={(e) => setForm({ ...form, is_adult: e.target.checked })} data-testid="product-form-adult" />
                  Produit réservé aux adultes (18+)
                </label>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setForm(null)}>Annuler</ABtn>
              <ABtn onClick={save} disabled={busy} data-testid="product-form-save">Enregistrer</ABtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const NumField = ({ label, value, onChange, testid }) => (
  <div>
    <ALabel>{label}</ALabel>
    <AInput type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);
